import React from "react";
import ModifyAllTagsToNewTagSystemDialog from "../components/dialogs/ModifyAllTagsToNewTagSystemDialog";
import SettingsPage from "../components/SettingsPane/SettingsPage";
import { PrefixMode } from "../models/SettingsEnums";
import { CheckboxFieldSettings } from "../SettingsScreen";
import { itx } from "../translationItx/itx";
import Dropdown from "react-dropdown-select";
import NameAndTagManager from "../NameAndTagManager";
import BaseModel from "../models/BaseModel";
import { showToast } from "../components/Toasts";
import { isUiModeDeveloper } from "../uiMode";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";

function ColorPickerRow(props: {
  label: string;
  value: string | undefined;
  defaultColor: string;
  onChange: (color: string) => void;
  onReset: () => void;
}) {
  const { label, value, defaultColor, onChange, onReset } = props;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span style={{ minWidth: "110px" }}>{label}</span>
      <input
        key={value ?? "default"}
        type="color"
        defaultValue={value || defaultColor}
        style={{ width: "40px", height: "32px", padding: "0", border: "none", borderRadius: "0", cursor: "pointer" }}
        onChange={(ev) => onChange(ev.target.value)}
      />
      <button
        data-tooltip-id="tv-tooltip"
        data-tooltip-content={itx("SettingsScreen.resetColor", "Zurücksetzen")}
        data-tooltip-place="top"
        className="button circle small"
        onClick={onReset}
      >
        <i className="las la-undo-alt" />
      </button>
    </div>
  );
}

export default function SettingPageDarstellung(props: {
  handler: string;
  dropDownColor: string;
  setRefresh: (number: number) => void;
  refresh?: number;
}) {
  const { dropDownColor, setRefresh, refresh } = props;
  useOnChangeWatcher(state.undoManager);
  // Keep the TAG generator out of the startup dependency graph (Device → Button).
  const loadC1Tagging = () => import("../../ConceptOne/src/estada/C1_generateTags");
  function ConsumerPräfixElm(props: {}) {
    const prefixObj = [
      { name: itx("toolBar.switchShort", "Leuchte geschaltet"), value: "switch" },
      { name: itx("toolBar.dimmShort", "Leuchte gedimmt"), value: "dimming" },
      { name: itx("toolBar.twShort", "Leuchte TW"), value: "tw" },
      { name: itx("toolBar.blindShort", "Rollo"), value: "blind" },
      { name: itx("toolBar.jalousieShort", "Jalousie"), value: "jalousie" },
      { name: itx("toolBar.heatingShort", "Heizen"), value: "heating" },
      { name: itx("toolBar.outletShort", "Steckdose"), value: "outlet" },
      { name: itx("toolBar.switchOutletShort", "Steckd. geschaltet"), value: "switchOutlet" },
      { name: itx("toolBar.ButtonShort", "Taster"), value: "button" },
      { name: itx("toolBar.SensorShort", "Sensor"), value: "sensor" },
      { name: itx("toolBar.inputLock", "Input Lock"), value: "input" },
      { name: itx("toolBar.lan", "LAN"), value: "lan" },
      { name: itx("toolBar.intermediateDistribution", "Raum-Unterverteilung"), value: "intermediateDistribution" },
    ];
    const useDevicePrefixValues = ["button", "sensor"]; // no itx

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "max-content 54px",
          gap: "5px 10px",
          alignItems: "center",
        }}
      >
        {prefixObj.map((obj) => {
          const defaultValue = useDevicePrefixValues.includes(obj.value)
            ? (state.settings.devicePrefix as any)[obj.value]
            : (state.settings.consumerPrefix as any)[obj.value];
          return (
            <React.Fragment key={obj.value}>
              <span style={{ whiteSpace: "nowrap" }}>{obj.name}</span>
              <input
                style={{
                  width: "54px",
                  borderRadius: "4px",
                  border: "1px solid var(--border-color)",
                  padding: "3px 6px",
                }}
                key={obj.name + "_texField_input"} // no itx
                type="text"
                className="input"
                name={obj.name}
                id={obj.value}
                maxLength={state.maxCharLimit.tag}
                onChange={(ev) => {
                  if (ev.target.value.length < 4) {
                    if (useDevicePrefixValues.includes(obj.value)) {
                      (state.settings.devicePrefix as any)[obj.value] = ev.target.value.toUpperCase();
                    } else {
                      (state.settings.consumerPrefix as any)[obj.value] = ev.target.value.toUpperCase();
                    }
                  }
                  if (setRefresh && refresh) setRefresh(refresh + 1);
                }}
                defaultValue={defaultValue}
                placeholder={defaultValue}
              />
            </React.Fragment>
          );
        })}
      </div>
    );
  }
  function FuseAndELCBsElm() {
    const prefixFields = [
      { key: "elcb", label: itx("SettingsScreen.elcbPrefix", "FI") },
      { key: "fuse", label: itx("SettingsScreen.fusePrefix", "Sicherung") },
    ] as const;

    function changeSchema(schema: "A" | "B" | "C") {
      if (state.settings.fuseTagSchema === schema) return;
      state.settings.beforeChange("fuseTagSchema");
      state.settings.fuseTagSchema = schema;
      state.settings.changed("fuseTagSchema");
      setRefresh((refresh ?? 0) + 1);
    }

    function changeFullTagIncludesElcb(includeElcbTag: boolean) {
      if (state.settings.includeElcbTagInFuseFullTag === includeElcbTag) return;
      state.settings.beforeChange("includeElcbTagInFuseFullTag");
      state.settings.includeElcbTagInFuseFullTag = includeElcbTag;
      state.settings.changed("includeElcbTagInFuseFullTag");
      setRefresh((refresh ?? 0) + 1);
    }

    return (
      <div className="settings-fuse-tag-settings">
        <div className="settings-fuse-tag-settings__prefixes">
          {prefixFields.map(({ key, label }) => (
            <label className="settings-fuse-tag-settings__prefix" key={key} htmlFor={`electricalTagPrefix-${key}`}>
              <span>{label}</span>
              <input
                id={`electricalTagPrefix-${key}`}
                type="text"
                className="input"
                maxLength={state.maxCharLimit.tag}
                value={state.settings.devicePrefix[key]}
                onChange={(event) => {
                  const value = event.target.value.toUpperCase();
                  state.settings.beforeChange("devicePrefix");
                  state.settings.devicePrefix[key] = value;
                  state.settings.changed("devicePrefix");
                  setRefresh((refresh ?? 0) + 1);
                }}
              />
            </label>
          ))}
        </div>

        <div className="settings-fuse-tag-settings__schemas" role="radiogroup" aria-label={itx("SettingsScreen.fuseTagSchema", "Nummerierung der Sicherungen")}>
          {(
            [
              ["A", itx("SettingsScreen.fuseTagSchemaA", "Sicherungen projektweit durchgehend nummerieren")],
              ["B", itx("SettingsScreen.fuseTagSchemaB", "Sicherungen pro FI ab 1 nummerieren")],
              ["C", itx("SettingsScreen.fuseTagSchemaC", "Sicherungen und FI gemeinsam durchgehend nummerieren")],
            ] as const
          ).map(([schema, label]) => (
            <label className="settings-fuse-tag-settings__schema" key={schema}>
              <input
                type="radio"
                name="fuseTagSchema"
                value={schema}
                checked={state.settings.fuseTagSchema === schema}
                onChange={() => changeSchema(schema)}
              />
              <span className="settings-fuse-tag-settings__schema-text">
                <strong>{schema}</strong>
                <span>{label}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="form-label-bold">
          {itx("SettingsScreen.fuseFullTag", "Vollständiges Sicherungskennzeichen")}
        </div>
        <div
          className="settings-fuse-tag-settings__schemas"
          role="radiogroup"
          aria-label={itx("SettingsScreen.fuseFullTag", "Vollständiges Sicherungskennzeichen")}
        >
          {(
            [
              [
                true,
                itx(
                  "SettingsScreen.fuseFullTagWithElcb",
                  "FI-Kennzeichen voranstellen (z. B. Q1.F1)",
                ),
              ],
              [
                false,
                itx(
                  "SettingsScreen.fuseFullTagWithoutElcb",
                  "Nur das Sicherungs-Kennzeichen anzeigen (ohne übergeordnetes FI-Kennzeichen)",
                ),
              ],
            ] as const
          ).map(([includeElcbTag, label]) => (
            <label className="settings-fuse-tag-settings__schema" key={String(includeElcbTag)}>
              <input
                type="radio"
                name="fuseFullTag"
                value={String(includeElcbTag)}
                checked={state.settings.includeElcbTagInFuseFullTag === includeElcbTag}
                onChange={() => changeFullTagIncludesElcb(includeElcbTag)}
              />
              <span className="settings-fuse-tag-settings__schema-text settings-fuse-tag-settings__schema-text--full-width">
                <span>{label}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  function ReTagElements() {
    // to reset the tags of different elements
    const options = [
      { value: "Floors", label: itx("components.plural.Floor", "Floors") },
      { value: "Rooms", label: itx("components.plural.Room", "Rooms") },
      { value: "Actors", label: itx("components.plural.Actor", "Actors") },
      { value: "Buttons", label: itx("components.plural.Button", "Buttons") },
      { value: "Sensors", label: itx("components.plural.Sensor", "Sensors") },
      { value: "decentral Actors", label: itx("components.plural.DecentralActor", "decentral Actors") },
      { value: "Consumers", label: itx("components.plural.Consumer", "Consumers") },
      { value: "ELCBs", label: itx("components.plural.ELCB", "ELCBs") },
      { value: "Fuses", label: itx("components.plural.Fuse", "Fuses") },
      { value: "Multiplexers", label: itx("components.plural.Multiplexer", "Multiplexers") },
      { value: "Scenes", label: itx("components.plural.Scene", "Scenes") },
    ]; // no itx

    const [selected, setSelected] = React.useState<Set<string>>(new Set(options.map((opt) => opt.value)));
    const allSelected = selected.size === options.length;
    const someSelected = selected.size > 0 && !allSelected;

    function toggleAll() {
      setSelected(allSelected ? new Set() : new Set(options.map((opt) => opt.value)));
    }

    function toggle(opt: string) {
      setSelected((prev) => {
        const next = new Set(prev);
        next.has(opt) ? next.delete(opt) : next.add(opt);
        return next;
      });
    }

    function renumberSelectedObjects() {
      function renumber(prefix: string, objects: BaseModel[]) {
        state.nameAndTagManager.renumberTagObjs(
          objects.sort((a, b) => a.tag.localeCompare(b.tag, undefined, { numeric: true })),
          prefix,
        );
      }
      selected.forEach((opt) => {
        switch (opt) {
          case "Floors":
            // prefix from settings not availiable->not set onlyfirst character of translated word
            renumber(itx("components.Floor", "Floors").charAt(0), state.floors.asArray());
            break;
          case "Rooms":
            // prefix from settings not availiable->not set onlyfirst character of translated word
            renumber(
              itx("components.Room", "Rooms").charAt(0),
              state.allRooms().filter((r) => !r.isFloorGeneralRoom),
            );
            break;
          case "Actors":
            renumber(state.settings.devicePrefix.actor, state.allActors());
            break;
          case "Buttons":
            renumber(
              state.settings.devicePrefix.button,
              state.allButtons().filter((b) => !b.isSensor || !b.isDecentralActor),
            );
            break;
          case "Sensors":
            renumber(
              state.settings.devicePrefix.sensor,
              state.allButtons().filter((b) => b.isSensor),
            );
            break;
          case "decentral Actors":
            renumber(
              state.settings.devicePrefix.decentralActor,
              state.allButtons().filter((b) => b.isDecentralActor),
            );
            break;
          case "Consumers":
            let consumerSortet = new Map<string, BaseModel[]>();
            state.allConsumers().forEach((c) => {
              const prefix = state.nameAndTagManager.getPrefixForChannelType(c.prefixType);
              let consumers = consumerSortet.get(prefix) || [];
              consumers.push(c);
              consumerSortet.set(prefix, consumers);
            });
            consumerSortet.forEach((consumers, prefix) => {
              renumber(prefix, consumers);
            });

            // mor complicated because of different prefixes and consumer types
            // [ChannelType.Switch, state.settings.consumerPrefix.switch],
            // [ChannelType.Dimmer, state.settings.consumerPrefix.dimming],-
            // [ChannelType.StaircaseLight, state.settings.consumerPrefix.staircaseLight],
            // [ChannelType.TuneableWhite, state.settings.consumerPrefix.tw],
            // [ChannelType.Rgb, state.settings.consumerPrefix.rgb],
            // //  [ChannelType.Rgbw, state.settings.consumerPrefix.rgbw],
            // [ChannelType.Heating, state.settings.consumerPrefix.heating],
            // [ChannelType.Jalousie, state.settings.consumerPrefix.jalousie],
            // [ChannelType.Blind, state.settings.consumerPrefix.blind],
            // [ChannelType.PlugSwitchable, state.settings.consumerPrefix.switchOutlet],
            // [ChannelType.Plug, state.settings.consumerPrefix.outlet],
            // [ChannelType.Lock, state.settings.consumerPrefix.input],

            // [ChannelType.suply12V, state.settings.consumerPrefix.s12],
            // [ChannelType.suply24V, state.settings.consumerPrefix.s24],
            // [ChannelType.suply230V, state.settings.consumerPrefix.e230],
            // [ChannelType.suply400V, state.settings.consumerPrefix.e400],

            break;
          case "ELCBs": {
            if (state.settings.fuseTagSchema === "C") {
              state.nameAndTagManager.renumberFuseTags();
            } else {
              state.nameAndTagManager.renumberElcbs();
            }
            break;
          }
          case "Fuses": {
            state.nameAndTagManager.renumberFuseTags();
            break;
          }
          case "Multiplexers":
            renumber(state.settings.devicePrefix.multiplexer, state.allMultiplexers());
            break;
          case "Scenes":
            state
              .allScenes()
              .filter((s) => s.className === "Scene")
              .forEach((scene, index) => {
                // no tag ->the sceneNumber
                scene.sceneNumber = index + 1;
              });
            break;
        }
      });
      showToast({ text: itx("SettingsScreen.renumberingDone", "Neu nummerieren abgeschlossen") });
    }

    return (
      <div className="settings-card">
        <div className="settings-card__header">
          {itx("SettingsScreen.reTagElements", "Kennzeichen neu nummerieren")}
        </div>
        <div className="settings-card__body">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: 600,
              cursor: "pointer",
              paddingBottom: "8px",
              borderBottom: "1px solid var(--border-color)",
            }}
          >
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={toggleAll}
            />
            {allSelected
              ? itx("SettingsScreen.deselectAll", "Alle abwählen")
              : itx("SettingsScreen.selectAll", "Alle auswählen")}
          </label>
          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "6px 16px" }}
          >
            {options.map((opt) => (
              <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                <input type="checkbox" checked={selected.has(opt.value)} onChange={() => toggle(opt.value)} />
                {opt.label}
              </label>
            ))}
          </div>
          <button
            type="button"
            className="button white"
            style={{ marginTop: "4px", alignSelf: "flex-end" }}
            onClick={() => {
              renumberSelectedObjects();
            }}
          >
            {itx("SettingsScreen.renumber", "Neu nummerieren")}
          </button>
        </div>
      </div>
    );
  }
  const prefixModeButtons = [
    { value: PrefixMode.DIN, label: itx("SettingsScreen.din", "DIN") },
    { value: PrefixMode.DOT, label: itx("SettingsScreen.simple", "Einfach") },
  ].map(({ value, label }) => (
    <button
      key={value}
      type="button"
      className={state.settings.prefixMode === value ? "active" : ""}
      aria-pressed={state.settings.prefixMode === value}
      onClick={() => {
        state.settings.prefixMode = value;
        setRefresh?.((refresh ?? 0) + 1);
      }}
    >
      {label}
    </button>
  ));

  return (
    <SettingsPage handler="/settings/darstellung">
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* ── Farben ───────────────────────────────────────────────── */}

        <div className="form-flex" style={{ gap: "20px", alignItems: "flex-start" }}>
          {/* ── Präfix-Einstellungen ─────────────────────────────────── */}
          <div style={{ display: "flex", flexDirection: "column", gap: "20px", flex: "1 1 0", minWidth: 0 }}>
            <section className="settings-card">
              <h3 className="settings-card__header">{itx("SettingsScreen.tags", "Tags")}</h3>
              <div className="settings-card__body">
                <CheckboxFieldSettings
                  name="uniqueTagsForProject" // no itx
                  label={itx("SettingsScreen.tagsUniqueProjectWide", "Projektweit eindeutig")}
                  onChange={(newValue) => {
                    state.dialogManager.openDialog(<ModifyAllTagsToNewTagSystemDialog newValue={newValue} />);
                  }}
                  valueFromProject={true}
                  setRefresh={setRefresh}
                  refresh={refresh}
                />
                {state.projectMetadata.isC1Project && (
                  <>
                    <CheckboxFieldSettings
                      name="c1TagByIndex"
                      label={itx("c1.ui.Tags_nach_MasterIndex_gruppieren", "Tags nach MasterIndex gruppieren")}
                      onChange={async () => {
                        const project = state.c1Project;
                        // Settings are also imported while Device is being initialized.
                        const { generateTags } = await loadC1Tagging();
                        if (state.c1Project === project) {
                          generateTags(project);
                          setRefresh((refresh ?? 0) + 1);
                        }
                      }}
                      setRefresh={setRefresh}
                      refresh={refresh}
                    />
                    <label className="form-group" style={{ display: "flex", flexDirection: "row", height: "auto", alignItems: "center", paddingTop: "10px" }}>
                      <input
                        id="c1TagsFrozen"
                        type="checkbox"
                        style={{ height: "1em", marginRight: "10px" }}
                        checked={
                          state.c1Project.data.tagNumbering?.frozen ??
                          Boolean(state.c1Project.data.tagNumbering || state.settings.applicationMode === "main")
                        }
                        onChange={async (event) => {
                          const value = event.target.checked;
                          const project = state.c1Project;
                          const { setTagsFrozen } = await loadC1Tagging();
                          if (state.c1Project !== project) return;
                          state.undoManager.runAsSingleStep(
                            itx("c1.tags.freezeUndo", "Tags einfrieren / neu berechnen"),
                            () => setTagsFrozen(project, value),
                          );
                          setRefresh((refresh ?? 0) + 1);
                          if (!value)
                            void showToast({
                              text: itx(
                                "c1.tags.recalculated",
                                "Alle Tags wurden neu berechnet. Die Änderung kann rückgängig gemacht werden.",
                              ),
                            });
                        }}
                      />
                      {itx("c1.tags.frozen", "Tags eingefroren")}
                    </label>
                    <CheckboxFieldSettings
                      name="c1TagEditable"
                      label={itx("c1.ui.Tags_editierbar", "Tags editierbar")}
                      setRefresh={setRefresh}
                      refresh={refresh}
                    />
                  </>
                )}
              </div>
            </section>
            <section className="settings-card">
              <h3 className="settings-card__header">{itx("SettingsScreen.structuralElements", "Strukturelemente")}</h3>
              <div className="settings-card__body">
                <div className="settings-field-row">
                  <span>{itx("SettingsScreen.prefixLabel", "Präfix")}</span>
                  <div
                    className="settings-choice"
                    role="group"
                    aria-label={itx("SettingsScreen.prefixLabel", "Präfix")}
                  >
                    {prefixModeButtons}
                  </div>
                </div>
              </div>
            </section>
            <section className="settings-card">
              <h3 className="settings-card__header">{itx("SettingsScreen.consumerPrefix", "Funktionen Präfixe")}</h3>
              <div className="settings-card__body">
                <ConsumerPräfixElm />
              </div>
            </section>
            <section className="settings-card">
              <h3 className="settings-card__header">
                {itx("SettingsScreen.FuseAndELCBs", "TAG-Einstellungen für FI, Sicherungen und FI/LS")}
              </h3>
              <div className="settings-card__body">
                <FuseAndELCBsElm />
              </div>
            </section>
          </div>

          {/* ── PDF-Einstellungen + ReTag ────────────────────────────── */}
          <div style={{ display: "flex", flexDirection: "column", gap: "20px", flex: "1 1 0", minWidth: 0 }}>
            {!state.projectMetadata.isC1Project && state.projectLicense.checkIfProfessionalAbo() && (
              <div className="settings-card">
                <div className="settings-card__header">{itx("SettingsScreen.pdf", "PDF")}</div>
                <div className="settings-card__body">
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <label className="form-label-bold">
                      {itx("SettingsScreen.headerFooterColors", "Kopf- und Fußzeile")}
                    </label>

                    <ColorPickerRow
                      label={itx("SettingsScreen.backgroundColor", "Hintergrundfarbe")}
                      value={state.settings.uiBackgroundColor}
                      defaultColor="#e57e04" // no itx
                      onChange={(color) => {
                        state.settings.uiBackgroundColor = color;
                      }}
                      onReset={() => {
                        state.settings.uiBackgroundColor = "#e57e04";
                        if (setRefresh && refresh) setRefresh(refresh + 1);
                      }}
                    />

                    <ColorPickerRow
                      label={itx("SettingsScreen.textColor", "Textfarbe")}
                      value={state.settings.uiTextColor}
                      defaultColor="white" // no itx
                      onChange={(color) => {
                        state.settings.uiTextColor = color;
                      }}
                      onReset={() => {
                        state.settings.uiTextColor = "white";
                        if (setRefresh && refresh) setRefresh(refresh + 1);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            <ReTagElements />
          </div>
        </div>
      </div>
    </SettingsPage>
  );
}
